<template>
	<div :id="props.context.id" ref="selectboxRef" class="relative" :class="props.context.classes.inner">
		<div class="relative">
			<button
				ref="selectboxButton"
				type="button"
				class="rounded-lg border border-border-primary text-sm py-2.5 px-5 pr-3 text-body-secondary flex justify-between items-center w-full truncate min-h-[2.875rem] transition duration-300"
				:class="[...props.context.classes.input, !isDropdownOpen ? 'bg-background-secondary' : 'bg-white']"
				:aria-expanded="isDropdownOpen"
				:disabled="!!context.disabled"
				@click="enableSearchMode ? isDropdownOpen = true : isDropdownOpen = !isDropdownOpen"
			>
				<template v-if="enableSearchMode && isDropdownOpen">
					<NuxtIcon
						name="search"
						class="text-2xl text-icon-primary transition-transform mr-2 -ml-1"
						filled
					/>
					<input
						ref="input-search"
						v-model="inputSearchValue"
						v-focus
						type="text"
						:placeholder="selectionText"
						class="w-full bg-transparent focus:outline-hidden"
					/>
				</template>
				<template v-else>
					<div class="flex items-center gap-2 lg:gap-4">
						<ClientOnly>
							<component
								:is="context.slots.prefixCustomIcon"
								v-if="context.slots.prefixCustomIcon"
								v-bind="props.context"
							/>
						</ClientOnly>
						{{ selectionText }}
					</div>
					<ClientOnly>
						<component
							:is="context.slots.selectIcon"
							v-if="context.slots.selectIcon"
							v-bind="props.context"
						/>
						<NuxtIcon
							v-else
							name="chevron-down"
							class="text-2xl text-icon-primary transition-transform"
							:class="[props.context.classes.inputIcon, {'rotate-180': isDropdownOpen}]"
							filled
						/>
					</ClientOnly>
				</template>
			</button>
			<button
				v-if="enableSearchMode && isDropdownOpen"
				type="button"
				class="absolute top-1/2 right-3.5 -translate-y-1/2"
				@click="clear()"
			>
				<NuxtIcon
					name="x"
					class="text-2xl text-icon-primary transition-transform"
					filled
				/>
			</button>
		</div>
		<ClientTeleport>
			<div
				v-show="isDropdownOpen"
				ref="selectboxDropdownRef"
				:class="['absolute z-dropdown rounded-lg bg-background-primary py-3 px-5 w-full max-h-[17rem] overflow-y-auto shadow-xl min-w-60 space-y-1', props.context.classes.dropdown]"
				:style="{
					...floatingStyles,
					width: `auto`,
				}"
				@scroll="onScroll"
				@keydown.esc.prevent="closeAndFocusToggle"
			>
				<ul>
					<li v-if="props.context.multiple" key="__allOption" class="my-2">
						<button
							type="button"
							class="flex items-center w-full rounded-lg focus-outline outline-3 hover:text-body-important"
							@click="toggleAll"
						>
							{{ props.context.allOptionsLabel || 'Všechny možnosti' }}
						</button>
						<hr class="w-full border-t border-border-secondary mt-3" />
					</li>
					<li v-else-if="props.context.clearable" key="__noOption">
						<button
							type="button"
							class="flex items-center w-full rounded-lg focus-outline outline-3 hover:text-body-important"
							@click="clear"
						>
							{{ props.context.clearLabel || 'Zrušit výběr' }}
						</button>
						<hr class="w-full border-t border-border-secondary mt-3" />
					</li>
					<li
						v-for="(option, i) in visibleOptions"
						:key="i"
						:tabindex="option.sectionHeader ? undefined : 0"
						class="relative flex items-center rounded-lg focus-outline outline-3 text-body-secondary px-4 py-3"
						:class="[
							option.sectionHeader
								? 'cursor-default'
								: option.disabled
									? 'cursor-not-allowed hover:bg-transparent'
									: !isSelected(props, option) && 'cursor-pointer hover:text-body-primary hover:bg-background-noticeable',
						]"
						@keydown.space.prevent="!option.sectionHeader && simulateClick($event)"
						@mouseenter="!option.sectionHeader && (hoveredIndex = i)"
						@mouseup="!option.sectionHeader && (pressedIndex = null)"
						@mousedown="!option.sectionHeader && (pressedIndex = i)"
						@mouseleave="() => { hoveredIndex = null; pressedIndex = null }"
						@click="!option.sectionHeader && onOptionClick(option)"
					>
						<template v-if="option.sectionHeader">
							<div class="typo-body-s font-normal text-neutral-800 flex items-center gap-2 w-full">
								<NuxtIcon
									v-if="option.icon"
									:name="option.icon"
									filled
									aria-hidden="true"
								/>
								{{ option.label }}

								<Tooltip v-if="option.info" class="ml-auto" bubble-class="!max-w-[15rem]" right-arrow>
									<template #default>
										<span class="text-xs text-body-highlight underline underline-offset-1 flex items-center gap-1">
											{{ option.info.title }}
											<NuxtIcon name="info" filled class="text-body-highlight cursor-help" />
										</span>
									</template>
									<template #tooltip>
										{{ option.info.tooltip }}
									</template>
								</Tooltip>
							</div>
						</template>
						<template v-else>
							<input
								:id="getInputId(props, option)"
								:type="props.context.multiple ? 'checkbox' : 'radio'"
								:name="props.context.name"
								:value="option"
								:checked="isSelected(props, option)"
								:disabled="option.disabled"
								class="hidden"
								@change="toggleOption(option, $event)"
							/>
							<SelectboxOptionCheckbox
								v-if="props.context.multiple"
								:checked="isSelected(props, option)"
								:hover="hoveredIndex === i"
								:pressed="pressedIndex === i"
								class="shrink-0"
							/>
							<label
								:for="getInputId(props, option)"
								class="before:cover-parent focus-outline"
								:class="option.disabled ? 'cursor-not-allowed text-body-quaternary' : 'cursor-pointer'"
							>
								{{ option.label }}
							</label>
							<span v-if="option.disabledText" class="ml-2 text-xs text-body-quaternary">
								{{ option.disabledText }}
							</span>
						</template>
					</li>
					<li v-if="!visibleOptions.length && enableSearchMode" class="text-center">
						<NuxtIcon name="frown" filled class="text-4xl mb-2 text-icon-primary text-center inline-block" />
						<span class="text-xs max-w-48 block">
							Nenašli jsme žádné možnosti, které by odpovídaly vašemu vyhledávání.
						</span>
					</li>
				</ul>
			</div>
		</ClientTeleport>
	</div>
</template>

<script setup lang="ts">
	import {useFloating, offset, shift, flip, size, autoUpdate} from '@floating-ui/vue';
	import {keyBy, map, without} from 'lodash-es';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {getInputId, isSelected} from '~/utils/formkitSelectbox';
	import {container} from '~/config/tailwind';
	import {useRemToPx} from '~/composables/useRemToPx';
	import {useSelectboxStore} from '~/stores/selectbox';
	import type {Option, SelectboxContext} from '@/types/formkitSelectbox';

	type Props = {
		context: SelectboxContext;
	};

	const props = defineProps<Props>();

	const isDropdownOpen = ref(false);

	const hoveredIndex = ref<number | null>(null);
	const pressedIndex = ref<number | null>(null);

	const keyedOptions = computed(() => keyBy(props.context.options, 'value'));

	const selection = computed(() => {
		if (props.context.multiple) {
			const firstOption = keyedOptions.value[props.context._value[0]];

			if (!firstOption) {
				return '';
			}

			return props.context._value.length > 1
				? `${firstOption.label} + ${props.context._value.length - 1}`
				: firstOption.label;
		}

		return keyedOptions.value[props.context._value as string]?.label || '';
	});

	const selectionText = computed(() => (
		props.context._value?.length
			? selection.value
			: props.context.placeholder || ''
	));

	const selectableOptions = computed(() => (props.context.options ?? []).filter((option: Option) => !option.sectionHeader));

	const areAllOptionsSelected = computed(() => {
		if (!props.context.multiple || !selectableOptions.value.length) {
			return false;
		}

		const valueLength = props.context._value?.length ?? 0;
		return valueLength >= selectableOptions.value.length;
	});

	function toggleOption(option: Option, state: Event) {
		if (option.disabled) {
			return;
		}

		if (props.context.multiple) {
			const newValue = (state.target as HTMLInputElement).checked
				? [...props.context._value, option.value]
				: without(props.context._value, option.value);

			props.context.node.input(newValue);
		} else {
			props.context.node.input(option.value);
		}

		if (!props.context.multiple) {
			isDropdownOpen.value = false;
		}
	}

	function toggleAll() {
		props.context.node.input(
			areAllOptionsSelected.value
				? []
				: map(selectableOptions.value, 'value'),
		);
	}

	function clear() {
		props.context.node.input([]);
		isDropdownOpen.value = false;
	}

	const selectboxStore = useSelectboxStore();
	const selectboxRef = ref<HTMLElement | null>(null);
	const selectboxDropdownRef = ref<HTMLUListElement | null>(null);
	const selectboxButton = ref<HTMLButtonElement | null>(null);

	const enableSearchMode = computed(() => props.context.attrs.search);
	const inputSearchRef = templateRef('input-search');
	const inputSearchValue = ref('');
	const {activate: focusTrapActive, deactivate: focusTrapDeactivate} = useFocusTrap([selectboxRef, selectboxDropdownRef], {
		escapeDeactivates: true,
		initialFocus: () => enableSearchMode.value ? inputSearchRef.value : selectboxDropdownRef.value,
	});

	onClickOutside(selectboxRef, () => {
		isDropdownOpen.value = false;
	}, {ignore: [selectboxDropdownRef]});

	const dropdownWidth = ref(0);
	const currentBreakpoint = useCurrentBreakpoint();
	const containerPadding = ref(0);

	watch(currentBreakpoint, () => {
		const size = (currentBreakpoint.value ?? 'DEFAULT') as keyof typeof container.padding;
		containerPadding.value = useRemToPx(container.padding[size]);
	});

	const {floatingStyles} = useFloating(selectboxButton, selectboxDropdownRef, {
		placement: props.context.attrs['dropdown-end'] === 'true' ? 'bottom-end' : 'bottom-start',
		whileElementsMounted: autoUpdate,
		middleware: [
			flip(),
			shift({padding: containerPadding.value}),
			offset(5),
			size({
				apply({availableWidth, rects}) {
					dropdownWidth.value = Math.min(availableWidth, rects.reference.width);
				},
			}),
		],
	});

	const visibleOptions = ref<Option[]>([]);
	const itemsPerPage = 100;
	const currentPage = ref(0);
	const vFocus = {mounted: (el: HTMLInputElement) => el.focus()};

	const filteredOptions = computed(() => {
		const normalize = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036F]/g, '').toLowerCase();
		const options = props.context.options ?? [];
		const searchValue = normalize(inputSearchValue.value);
		const filteredResult: Option[] = [];
		let activeSection: Option | null = null;
		let sectionBuffer: Option[] = [];

		function flushSection() {
			if (!sectionBuffer.length) {
				return;
			}

			if (activeSection) {
				filteredResult.push(activeSection);
			}

			filteredResult.push(...sectionBuffer);
			sectionBuffer = [];
		}

		for (const option of options) {
			if (option.sectionHeader) {
				flushSection();
				activeSection = option;
				continue;
			}

			if (normalize(option.label).includes(searchValue)) {
				sectionBuffer.push(option);
			}
		}

		flushSection();

		return filteredResult;
	});

	function loadMoreOptions() {
		const start = currentPage.value * itemsPerPage;
		const end = start + itemsPerPage;
		visibleOptions.value = filteredOptions.value.slice(0, end);
		currentPage.value++;
	}

	function onScroll(event: Event) {
		const target = event.target as HTMLElement;
		if (target.scrollTop + target.clientHeight >= target.scrollHeight - 100) {
			loadMoreOptions();
		}
	}

	function resetOptions() {
		currentPage.value = 0;
		visibleOptions.value = [];
		loadMoreOptions();
	}

	watch(filteredOptions, () => {
		resetOptions();
	});

	watch(isDropdownOpen, async (newValue) => {
		if (newValue) {
			resetOptions();

			await nextTick();
			// Focus first option in the dropdown
			// focusTrap.activate();
			focusTrapActive();
		} else {
			// focusTrap.deactivate();
			focusTrapDeactivate();

			await nextTick();
			inputSearchValue.value = '';
			currentPage.value = 0;
			visibleOptions.value = [];
			loadMoreOptions();
		}

		// See node_modules/@formkit/core/dist/index.d.ts:632
		// eslint-disable-next-line vue/no-mutating-props
		props.context.state.open = newValue;

		selectboxStore.setSelectboxDropdown(selectboxDropdownRef.value);
	});

	function simulateClick(e: Event) {
		const input = (e.target as HTMLElement)?.querySelector('input');
		if (input) {
			input.click();
		}
	}

	function closeAndFocusToggle() {
		isDropdownOpen.value = false;
		selectboxButton.value?.focus();
	}

	function onOptionClick(option: Option) {
		if (option.disabled) {
			return;
		}

		if (option.value === props.context._value) {
			props.context.node.emit('sameOption', option.value);
			isDropdownOpen.value = false;
		}
	}
</script>
